import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, X, Zap, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import "./upgradeBanner.scss";

const DISMISS_KEY = "upgrade-banner-dismissed-at";

const TOGGLE_LABELS = {
  zoomV2: "Zoom V2",
  recordings: "Class Recordings",
  whatsapp: "WhatsApp Alerts",
  customDomain: "Custom Domain",
  aiMock: "AI Mock Interviews",
  aiLearning: "AI Learning",
  aiQuestionBank: "AI Question Bank",
  feeManagement: "Fee Management",
  enquiryManagement: "Enquiry Mgmt",
  interviews: "Interview Scheduling",
  codingPractice: "Coding Practice",
  assignments: "Assignments",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  enquiryFormBuilder: "Enquiry Form Builder",
};

const formatPrice = (amount) => {
  if (!amount || amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

/**
 * UpgradeBanner — Smart dashboard banner that highlights what the
 * institute gains by upgrading to the next plan tier.
 *
 * Dismissable with 30-day re-appearance via localStorage.
 * Only renders if there's actually a higher plan available.
 */
const UpgradeBanner = () => {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed recently (within last 30 days)
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < 30) {
        setDismissed(true);
        return;
      }
    }

    const fetchBanner = async () => {
      try {
        const { data } = await apiClient.get("/billing/my-plan");
        if (data.upgradePlans && data.upgradePlans.length > 0) {
          // Pick the first (cheapest) upgrade — usually the "next tier"
          const nextPlan = data.upgradePlans[0];
          const currentPlan = data.currentPlan;

          // Calculate new features that would be unlocked
          const newFeatures = Object.keys(TOGGLE_LABELS).filter(
            (k) => nextPlan[k] && !currentPlan?.[k]
          );

          // Calculate limit upgrades
          const limitUpgrades = [];
          const limits = ["maxStudentsPerBatch", "maxBatches", "maxCourses", "maxInstructors"];
          limits.forEach((key) => {
            const cur = currentPlan?.[key] || 0;
            const next = nextPlan[key] || 0;
            if (next > cur) {
              const label = key === 'maxStudentsPerBatch' ? 'students per batch' : key.replace("max", "").toLowerCase();
              limitUpgrades.push(`${next} ${label}`);
            }
          });

          // Calculate usage pressure — are any resources at 80%+?
          const usage = data.usage || {};
          let hasUsagePressure = false;
          Object.entries(usage).forEach(([, v]) => {
            if (v.max > 0 && v.used / v.max >= 0.8) {
              hasUsagePressure = true;
            }
          });

          setBannerData({
            currentPlanName: currentPlan?.name || "Free",
            nextPlan,
            newFeatures,
            limitUpgrades,
            hasUsagePressure,
            subscription: data.subscription,
          });
        }
      } catch {
        // Banner is non-critical — fail silently
      }
    };

    fetchBanner();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (dismissed || !bannerData) return null;

  const { nextPlan, newFeatures, limitUpgrades, hasUsagePressure, subscription } = bannerData;
  const isTrial = subscription?.status === "trial";

  // Smart headline based on context
  let headline = "Unlock more for your institute";
  let subtext = `Upgrade to ${nextPlan.name} and take your operations to the next level.`;

  if (isTrial) {
    headline = "Your trial is active — get the full experience";
    subtext = `Subscribe to ${nextPlan.name} to keep your data and unlock premium features.`;
  } else if (hasUsagePressure) {
    headline = "You're running out of room";
    subtext = `Upgrade to ${nextPlan.name} for more capacity and premium features.`;
  }

  // Pick top 3 selling points
  const highlights = [];
  if (limitUpgrades.length > 0) {
    highlights.push(`Up to ${limitUpgrades[0]}`);
  }
  newFeatures.slice(0, 2).forEach((f) => {
    highlights.push(TOGGLE_LABELS[f]);
  });
  while (highlights.length < 3 && limitUpgrades.length > highlights.length) {
    highlights.push(`Up to ${limitUpgrades[highlights.length]}`);
  }

  return (
    <div className="ub-banner">
      <div className="ub-banner__glow" />
      <div className="ub-banner__content">
        <div className="ub-banner__icon">
          <Sparkles size={20} />
        </div>
        <div className="ub-banner__text">
          <div className="ub-banner__headline">{headline}</div>
          <div className="ub-banner__sub">{subtext}</div>
          {highlights.length > 0 && (
            <div className="ub-banner__highlights">
              {highlights.map((h, i) => (
                <span key={i} className="ub-banner__highlight-item">
                  <Check size={12} /> {h}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="ub-banner__actions">
          <div className="ub-banner__price-tag">
            <span className="ub-banner__price-from">from</span>
            <span className="ub-banner__price-amount">{formatPrice(nextPlan.priceMonthly)}</span>
            <span className="ub-banner__price-period">/mo</span>
          </div>
          <button className="ub-banner__btn" onClick={() => navigate("/billing")}>
            <Zap size={14} /> View Plans <ArrowRight size={14} />
          </button>
        </div>
      </div>
      <button className="ub-banner__close" onClick={handleDismiss} title="Dismiss for 30 days">
        <X size={14} />
      </button>
    </div>
  );
};

export default UpgradeBanner;
